import React from 'react';
import { Receipt, CheckCircle, Clock, AlertCircle, FileText } from 'lucide-react';

interface InvoiceStats {
  totalCount: number;
  totalAmount: number;
  paidCount: number;
  paidAmount: number;
  pendingCount: number;
  pendingAmount: number;
  overdueCount: number;
  overdueAmount: number;
  draftCount: number;
  draftAmount: number;
}

interface InvoiceStatsCardsProps {
  stats: InvoiceStats;
  isLoading?: boolean;
}

interface CircularProgressProps {
  percentage: number;
  color: string;
  icon: React.ReactNode;
}

const CircularProgress: React.FC<CircularProgressProps> = ({ percentage, color, icon }) => {
  const [animatedPercentage, setAnimatedPercentage] = React.useState(0);

  // Ensure percentage is between 0 and 100
  const normalizedPercentage = Math.max(0, Math.min(100, percentage));

  const size = 72;
  const radius = 32;
  const strokeWidth = 7;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (animatedPercentage / 100) * circumference;

  React.useEffect(() => {
    // Start animation after a brief delay
    const timer = setTimeout(() => {
      setAnimatedPercentage(normalizedPercentage);
    }, 100);

    return () => clearTimeout(timer);
  }, [normalizedPercentage]);

  return (
    <div className="relative flex items-center justify-center flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background circle - always 100% */}
        <circle
          stroke="#e5e7eb"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={size / 2}
          cy={size / 2}
          className="dark:stroke-gray-700"
        />
        {/* Foreground circle - percentage */}
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          r={normalizedRadius}
          cx={size / 2}
          cy={size / 2}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {icon}
      </div>
    </div>
  );
};

const InvoiceStatsCards: React.FC<InvoiceStatsCardsProps> = ({ stats, isLoading }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-6">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 animate-pulse"
          >
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          </div>
        ))}
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const cards = [
    {
      title: 'Total',
      count: stats.totalCount,
      amount: stats.totalAmount,
      icon: <Receipt className="h-5 w-5" />,
      iconColor: 'text-blue-600 dark:text-blue-400',
      chartColor: '#2563eb',
      percentage: 100, // Total is always 100%
    },
    {
      title: 'Paid',
      count: stats.paidCount,
      amount: stats.paidAmount,
      icon: <CheckCircle className="h-5 w-5" />,
      iconColor: 'text-green-600 dark:text-green-400',
      chartColor: '#16a34a',
      percentage: stats.totalCount > 0 ? (stats.paidCount / stats.totalCount) * 100 : 0,
    },
    {
      title: 'Pending',
      count: stats.pendingCount,
      amount: stats.pendingAmount,
      icon: <Clock className="h-5 w-5" />,
      iconColor: 'text-yellow-600 dark:text-yellow-400',
      chartColor: '#ca8a04',
      percentage: stats.totalCount > 0 ? (stats.pendingCount / stats.totalCount) * 100 : 0,
    },
    {
      title: 'Overdue',
      count: stats.overdueCount,
      amount: stats.overdueAmount,
      icon: <AlertCircle className="h-5 w-5" />,
      iconColor: 'text-red-600 dark:text-red-400',
      chartColor: '#dc2626',
      percentage: stats.totalCount > 0 ? (stats.overdueCount / stats.totalCount) * 100 : 0,
    },
    {
      title: 'Draft',
      count: stats.draftCount,
      amount: stats.draftAmount,
      icon: <FileText className="h-5 w-5" />,
      iconColor: 'text-gray-600 dark:text-gray-400',
      chartColor: '#6b7280',
      percentage: stats.totalCount > 0 ? (stats.draftCount / stats.totalCount) * 100 : 0,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 mb-6">
      {cards.map((card, index) => (
        <div
          key={index}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 transition-all hover:shadow-md"
        >
          <div className="flex items-start gap-4">
            <CircularProgress
              percentage={card.percentage}
              color={card.chartColor}
              icon={<div className={card.iconColor}>{card.icon}</div>}
            />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                {card.title}
              </h3>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {card.count} invoice{card.count !== 1 ? 's' : ''}
              </div>
              <div
                className="text-base font-bold text-gray-900 dark:text-gray-100 truncate cursor-default"
                title={formatCurrency(card.amount)}
              >
                {formatCurrency(card.amount)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default InvoiceStatsCards;
